import { FC } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { Button, Menu, MenuItem } from '@mui/material';

import { IdentityId } from '@playq/services-fingerprint';
import { AppID } from '@playq/octopus-common';
import { UserId } from '@playq/services-shared';

import { useModal } from '/hooks';
import {
  useDestroyUserDataOnAllServices,
  useDestroyUserDataOnAllServicesAllApps,
  useDroIndexpAttachmentsFor,
  useDropIndexAttachment,
  useUnbind,
} from '/api';
import { snackbarService } from '/common/snackbarService';
import { confirmDialog, ConfirmDialogType } from '/common/ConfirmDialog';
import { getUserIdFromUUID } from '/component/Support/Leaderboards/LboardInstanceScores/helpers';
import { appToolkit } from '/store';
import { useSearchQuery } from '/component/Support/BeetlesLookup/hook';
import { AttachmentsDialog } from '/component/Support/BeetlesLookup/Attachments/components';
import { IBeetleParams, IRange } from '/component/Support/BeetlesLookup/types';

import { MenuText } from './types';

export const MenuButton: FC<{
  type: 'attachments' | 'playerLookup';
  mainMenuText?: string;
  isIdentity?: IdentityId;
  roles?: boolean[];
}> = ({ type, roles, isIdentity, mainMenuText = 'Admin' }) => {
  const [isSuperAdmin = false, isAdmin = false] = roles || [];
  const app = useSelector(appToolkit.selectors.app);
  const appId = app?.id as AppID;
  const { range } = useSearchQuery();
  const { env } = useParams<IBeetleParams>();
  const { anchorEl, openAnchorEl, closeAnchorEl, open, isOpen, isOpenAnchorEl, close } = useModal();
  const { mutate: unbind } = useUnbind({
    onSuccess: () => {
      snackbarService.success('Unbind success ');
      close();
    },
  });
  const { mutate: destroyUserDataOnAllServicesAllApps } = useDestroyUserDataOnAllServicesAllApps({
    onSuccess: () => {
      snackbarService.success('User Data has been destroyed ');
      close();
    },
  });
  const { mutate: destroyUserDataOnAllServices } = useDestroyUserDataOnAllServices({
    onSuccess: () => {
      snackbarService.success('User Data has been destroyed ');
      close();
    },
  });
  const { mutate: dropIndex } = useDropIndexAttachment({
    onSuccess: () => {
      snackbarService.success('Index has been dropped');
    },
  });

  const showAttachmentDialog = () => {
    return confirmDialog({
      title: `Reindex attachments?`,
      content: 'This will drop index of attachments',
      type: ConfirmDialogType.Error,
      successButton: { label: 'Reindex' },
      onSuccess: () => dropIndex({ env }),
    });
  };
  const showConfirmDialog = ({ title, onSuccess }: { title: string; onSuccess: () => void }) => {
    return confirmDialog({
      title: `Are you sure you want to ${title}?`,
      content:
        'If you are deleting to comply with a regulatory request (GDPR, CCPA, etc) data may need to be cleaned up elsewhere to fully comply.',
      type: ConfirmDialogType.Error,
      onSuccess,
    });
  };
  const menuButtons = {
    attachments: [
      { text: MenuText.RE_INDEX, onClick: showAttachmentDialog, access: isSuperAdmin },
      {
        text: MenuText.RE_INDEX_FOR,
        onClick: open,
        access: isAdmin,
      },
    ],
    playerLookup: [
      {
        text: MenuText.UNBIND,
        onClick: () =>
          showConfirmDialog({
            title: MenuText.UNBIND,
            onSuccess: () => isIdentity && unbind({ identity: isIdentity, env }),
          }),
        access: true,
      },
      {
        text: MenuText.DESTROY_FROM_SERVICES,
        onClick: () =>
          showConfirmDialog({
            title: MenuText.DESTROY_FROM_SERVICES,
            onSuccess: () =>
              destroyUserDataOnAllServices({
                env,
                appId,
                userId: getUserIdFromUUID(isIdentity?.value) as UserId,
              }),
          }),
        access: true,
      },
      {
        text: MenuText.DESTROY_FROM_SERVICES_AND_APPS,
        onClick: () =>
          showConfirmDialog({
            title: MenuText.DESTROY_FROM_SERVICES_AND_APPS,
            onSuccess: () =>
              destroyUserDataOnAllServicesAllApps({
                env,
                userId: getUserIdFromUUID(isIdentity?.value) as UserId,
              }),
          }),
        access: true,
      },
    ],
  };
  const { mutate: dropIndexFor } = useDroIndexpAttachmentsFor({
    onSuccess: () => {
      snackbarService.success('Index has been dropped');
      close();
    },
  });
  const handleDropIndexFor = (localRange: IRange) => {
    return dropIndexFor({
      from_: localRange.from,
      to: localRange.to,
    });
  };
  return (
    <>
      <Button
        id='basic-button'
        disabled={false}
        variant='text'
        color='secondary'
        onClick={openAnchorEl}
        disableRipple={true}
      >
        {mainMenuText}
      </Button>

      <Menu
        variant='menu'
        id='fade-menu'
        MenuListProps={{
          'aria-labelledby': 'fade-button',
        }}
        sx={{ textTransform: 'capitalize' }}
        anchorEl={anchorEl}
        open={isOpenAnchorEl}
        onClose={closeAnchorEl}
      >
        {menuButtons[type]?.map((button) => {
          return (
            <MenuItem
              key={button.text}
              disabled={!button.access}
              onClick={() => {
                button.onClick();
                closeAnchorEl();
              }}
            >
              {button.text}
            </MenuItem>
          );
        })}
      </Menu>

      <AttachmentsDialog
        isOpen={isOpen}
        onClose={close}
        range={range}
        onClick={(localRange) => handleDropIndexFor(localRange)}
      />
    </>
  );
};
